import { TabDefinition } from '@/objects/Tabs';
import { Router } from '../Router';

export interface ITabContainer {
    /**
     * Get the active tab
     */
    GetActiveTab: TabDefinition | string;

    /**
     * Adds a tab to the tab container
     * @param tab
     * @param position where to insert the tab. If not provided the tab will be added to the end
     * @param selectTab if true, the tab will be selected after adding it
     */
    AddTab(tab: TabDefinition, position?: number, selectTab?: boolean): void;

    /**
     * Removes a tab from the tab container
     * @param tab
     */
    RemoveTab(tab: TabDefinition | string): void;

    /**
     * Sets the active tab based on the provided tab or tab name.
     * If the tab is not found, it will not change the active tab.
     * @param tab The tab definition or the name of the tab to set as active
     */
    SetActiveTab(tab: TabDefinition | string): void;

    /**
     * Checks if a tab with the given name exists in the tab container
     * @param tabName
     * @returns whether the tab container has a tab with the given name
     */
    HasTab(tabName: string): boolean;

    /**
     * Checks if a given tab is the currently active tab.
     * If no tab has been explicitly set as active when this is called, the first tab will be set as active
     * @param tab The tab to check if is active
     */
    isTabActive(tab: TabDefinition): boolean;

    // The tabs in the tab container
    Tabs: TabDefinition[];

    /**
     * Indicates if the tab container is for subtabs, this will not update the route parameter 'tab' or 'subtab'
     */
    isTabsInTab: boolean;
}

export class TabContainer implements ITabContainer {
    // The tabs in the tab container
    private tabs: TabDefinition[] = [];

    /**
     * The currently active tab
     */
    private activeTab: TabDefinition | null = null;

    /**
     * The name of the tab we want to preselect
     */
    private selectTab: string | null = null;

    /**
     * Constructor for the tab container
     * @param viewParams
     */
    public constructor() {
        this.selectTab = Router.RouteParams.subtab ?? Router.RouteParams.tab ?? null;
        this.isTabsInTab = false;
    }

    /**
     * Get the active tab
     */
    public get GetActiveTab() {
        if (!this.activeTab) {
            this.activeTab = this.tabs[0] || null;
        }
        return this.activeTab;
    }

    /**
     * Adds a tab to the tab container
     * @param tab
     * @param position where to insert the tab. If not provided the tab will be added to the end
     * @param selectTab if true, the tab will be selected after adding it
     */
    public AddTab(tab: TabDefinition, position?: number, selectTab?: boolean) {
        if (position !== undefined) {
            this.tabs.splice(position, 0, tab);
        } else {
            this.tabs.push(tab);
        }

        if (selectTab !== false && this.selectTab && this.selectTab == tab.Name) {
            this.activeTab = tab;
        }
    }

    /**
     * Checks if a given tab is the currently active tab.
     * If no tab has been explicitly set as active when this is called, the first tab will be set as active
     * @param tab The tab to check if is active
     */
    public isTabActive(tab: TabDefinition): boolean {
        return tab === this.GetActiveTab;
    }

    /**
     * Removes a tab from the tab container
     * @param tab
     */
    public RemoveTab(tab: TabDefinition | string) {
        let tabToRemove: TabDefinition | null = null;
        if (typeof tab === 'string') {
            tabToRemove = this.tabs.find((t) => t.Name == tab) ?? null;
        } else {
            tabToRemove = tab;
        }

        this.tabs = this.tabs.filter((t) => t != tabToRemove);
    }

    /**
     * Checks if a tab with the given name exists in the tab container
     * @param tabName
     * @returns
     */
    public HasTab(tabName: string): boolean {
        return this.tabs.some((t) => t.Name == tabName);
    }

    /**
     * Sets the active tab based on the provided tab or tab name.
     * If the tab is not found, it will not change the active tab.
     * @param tab The tab definition or the name of the tab to set as active
     */
    public SetActiveTab(tab: TabDefinition | string) {
        let selectedTab: TabDefinition | null = null;
        if (typeof tab === 'string') {
            selectedTab = this.tabs.find((t) => t.Name == tab) ?? null;
        } else {
            selectedTab = tab;
        }

        if (!selectedTab) {
            return;
        }

        this.setActiveTab(selectedTab);
    }

    /**
     * Sets the active tab and updates the route
     * @param tab
     */
    private setActiveTab(tab: TabDefinition) {
        this.activeTab = tab;

        if (this.isTabsInTab) {
            return;
        }

        const params = Router.RouteParams;

        if ((params.id ?? '') == '') {
            params.tab = tab.Name;
        } else if (Router.HasOpenModal) {
            params.subtab = tab.Name;
        }

        Router.NavigateTo(params);
    }

    /**
     * Get the tabs in the tab container
     */
    public get Tabs() {
        return this.tabs;
    }

    /**
     * Indicates if the tab container is for subtabs, this will not update the route parameter 'tab' or 'subtab'
     */
    public isTabsInTab: boolean;
}
